import type { Tutorial } from '../../shared/types.js';

export const membersTutorial: Tutorial = {
  name: 'members',
  title: 'Invite people to your org',
  description:
    'Walk through inviting teammates by email, how invites are delivered and redeemed, and where to manage members, roles, and pending invites.',
  category: 'organization',
  docPages: ['administration/organization-teams'],
  agenda: ['Manage the roster and roles', 'Track pending invites', 'Invite members by email'],
  steps: [
    {
      id: 'intro',
      kind: 'info',
      title: 'The Members page',
      body: "This is the roster for the active organization. This includes everyone with access, the roles they hold, and the invites still waiting to be redeemed. Let's walk through adding someone new.",
      path: '/configure/people/members',
    },
    {
      id: 'members-list',
      kind: 'info',
      title: 'Manage the roster',
      body: 'Everyone in the org shows up here. Use the roles control on each row to grant or revoke permissions, and the remove button to revoke access entirely.',
      target: '[data-testid="members-section"]',
      placement: 'top',
    },
    {
      id: 'pending-invites',
      kind: 'info',
      title: 'Track pending invites',
      body: 'Invites that have been sent but not yet redeemed live here, with when they were sent and when they expire. Revoke one any time to invalidate its link before someone uses it.',
      target: '[data-testid="pending-invites-section"]',
      placement: 'top',
    },
    // The invite steps come last because they open a modal: once the Add
    // member dialog is up, the page behind it is inert, so any step pointing
    // at the roster or the invite list would spotlight something the user
    // cannot reach.
    {
      id: 'add-member',
      kind: 'action',
      trigger: 'click',
      title: 'Open Add member',
      body: "**Add member** invites someone by email. You don't need them to have an account yet: the invite creates a one-time link they redeem to join **this** organization. Open it to continue.",
      target: '[aria-label="Add member"]',
      advanceWhen: '[data-testid="add-member-dialog"]',
      placement: 'bottom',
    },
    {
      id: 'invite-email',
      kind: 'action',
      trigger: 'input',
      title: "Enter the teammate's email",
      body: 'Type the email address of the person you want to invite. You can invite several people at once by separating addresses with commas. Type one to continue.',
      target: '[role="dialog"] [data-testid="invite-email-input"]',
      placement: 'bottom',
    },
    {
      id: 'invite-send',
      kind: 'info',
      title: 'Send the invite',
      body: 'Clicking **Send invite** creates a one-time redemption link for each person and emails it when your deployment has email set up. A dialog also shows each link so you can share it directly. The invite stays valid until it is redeemed or expires.',
      target: '[role="dialog"] [data-tutorial-id="invite-send"]',
      placement: 'bottom',
    },
    {
      id: 'done',
      kind: 'info',
      title: "That's inviting people",
      body: 'You now know how to invite teammates by email, share their one-time links, and manage members, roles, and pending invites for an organization.',
    },
  ],
  related: [{ tutorialName: 'organization', label: 'Tour the rest of the org' }],
};
